import React from 'react';
import styled from 'styled-components';
import { H1, Paragraph } from '@nab/nui-react';

export interface MaintenanceConfig {
    enabled: boolean;
    title?: string;
    message?: string;
    estimatedEnd?: string;
}

interface MaintenancePageProps {
    config: MaintenanceConfig;
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    padding: 40px 20px;
    text-align: center;
    background-color: ${({ theme }) => theme.color?.background?.primary ?? '#ffffff'};
`;

const IconWrapper = styled.div`
    margin-bottom: 24px;
    width: 64px;
    height: 64px;
`;

const MaintenanceIcon: React.FC = () => (
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path
            d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z"
            fill="currentColor"
        />
    </svg>
);

const Title = styled(H1)`
    color: ${({ theme }) => theme.color?.text?.brand ?? '#2d55a5'};
    margin-bottom: 16px;
`;

const Message = styled(Paragraph)`
    max-width: 560px;
    margin-bottom: 24px;
    color: ${({ theme }) => theme.color?.text?.primary ?? '#333333'};
    line-height: 1.6;
`;

const DEFAULT_TITLE = 'Scheduled Maintenance';
const DEFAULT_MESSAGE_WITH_DATE =
    'The Medfin Customer Portal is currently going through scheduled maintenance and is unavailable for use until {estimatedEnd}. We apologise for any inconvenience.';
const DEFAULT_MESSAGE_NO_DATE =
    'The Medfin Customer Portal is currently going through scheduled maintenance and is temporarily unavailable. We apologise for any inconvenience.';

export function getMaintenanceMessage(config: MaintenanceConfig): string {
    if (config.message) {
        return config.message;
    }
    if (config.estimatedEnd) {
        return DEFAULT_MESSAGE_WITH_DATE.replace('{estimatedEnd}', config.estimatedEnd);
    }
    return DEFAULT_MESSAGE_NO_DATE;
}

export const MaintenancePage: React.FC<MaintenancePageProps> = ({ config }) => {
    const title = config.title || DEFAULT_TITLE;
    const message = getMaintenanceMessage(config);

    return (
        <Container data-testid="maintenance-page">
            <IconWrapper>
                <MaintenanceIcon />
            </IconWrapper>
            <Title data-testid="maintenance-title">{title}</Title>
            <Message data-testid="maintenance-message">{message}</Message>
        </Container>
    );
};

export default MaintenancePage;
